// Group variant of MaterialAwareStartButtonV2 — one CAN MAKE button for a
// consolidated MULTI-WO group row.
//
// Availability comes from the group-aware material-requirements endpoint
// (`?work_orders=`), which sums every member tree's own physically-backed
// holdings plus the free global pool, so the figure is honest for the
// whole group (a single member's availability can't speak for the rest).
// Max producible = the bottleneck component's floor(available / per-unit),
// mirroring the backend's max_producible math.
//
// Same colours/labels as the single-WO button: green CAN MAKE N (covers
// everything), orange CAN MAKE N (partial), red SHORTAGE (none). Click
// forwards to onStart() — the group production dialog.

import React, { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { PlayArrow, Block } from '@mui/icons-material';
import { v2 } from '../../../../services/api';

export default function GroupMaterialAwareStartButtonV2({
  step, workOrderIds, remaining, onStart, testId,
}) {
  const [status, setStatus] = useState('loading');
  const [maxProducible, setMaxProducible] = useState(0);
  const [bottlenecks, setBottlenecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  const need = Math.max(0, parseFloat(remaining) || 0);
  const workOrdersKey = (workOrderIds || []).join(',');

  // Lazy-check on visibility, same as the single-WO button — group rows can
  // be numerous and each check is a query.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return undefined;
    let cancelled = false;
    async function check() {
      setLoading(true);
      try {
        const params = step?.step_id ? { step: step.step_id } : {};
        if (workOrdersKey) params.work_orders = workOrdersKey;
        const res = await v2.workOrders.materialRequirements(
          step.work_order_id, params,
        );
        if (cancelled) return;
        const reqs = res?.data?.requirements || [];
        let max = null;
        const perComponent = [];
        reqs.forEach((r) => {
          const per = parseFloat(r.required) || 0;
          if (per <= 0) return;
          const avail = Math.max(0, parseFloat(r.available) || 0);
          const fromThis = Math.floor(avail / per);
          perComponent.push({ name: r.item_name || r.item_code, fromThis });
          if (max == null || fromThis < max) max = fromThis;
        });
        if (max == null) max = need; // no component demand — unconstrained
        setMaxProducible(max);
        // Name the constraining component(s) when the group can't cover
        // its full need — surfaced in the tooltip.
        setBottlenecks(
          max != null && max < need
            ? perComponent.filter((p) => p.fromThis === max).map((p) => p.name)
            : [],
        );
        if (max <= 0) setStatus('none');
        else if (max >= need) setStatus('full');
        else setStatus('partial');
      } catch (err) {
        console.error('GroupMaterialAwareStartButtonV2: failed', err);
        if (!cancelled) setStatus('unknown');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    check();
    return () => { cancelled = true; };
  }, [isVisible, step?.work_order_id, step?.step_id, workOrdersKey, need]);

  const buttonColor = (() => {
    if (loading) return 'grey.500';
    switch (status) {
      case 'full': return 'success.main';
      case 'partial': return 'warning.main';
      case 'none': return 'error.main';
      default: return 'grey.500';
    }
  })();

  const buttonText = (() => {
    if (loading) return 'Checking...';
    // "x OF y": x capped at the group's remaining need, y = that need
    // tallied across every WO in the group.
    switch (status) {
      case 'full': return `CAN MAKE ${Math.min(maxProducible, need)} OF ${need}`;
      case 'partial': return `CAN MAKE ${maxProducible} OF ${need}`;
      case 'none': return 'SHORTAGE';
      default: return 'START';
    }
  })();

  const tooltipText = (() => {
    if (loading) return 'Checking material availability across the group...';
    const shortSuffix = bottlenecks.length
      ? ` — short of: ${bottlenecks.slice(0, 3).join(', ')}${bottlenecks.length > 3 ? '…' : ''}`
      : '';
    switch (status) {
      case 'full':
        return `All materials available — can make ${maxProducible} (${need} needed across ${(workOrderIds || []).length} WOs)`;
      case 'partial':
        return `Can make ${maxProducible} of ${need} needed across ${(workOrderIds || []).length} WOs${shortSuffix}`;
      case 'none':
        return `No materials available — production blocked${shortSuffix}`;
      default:
        return 'Material status unknown';
    }
  })();

  const isDisabled = loading || status === 'none' || need <= 0;

  return (
    <Tooltip title={tooltipText} arrow>
      <span ref={ref}>
        <Button
          variant="contained"
          size="small"
          data-testid={testId}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> :
            status === 'none' ? <Block /> :
            <PlayArrow />
          }
          onClick={(e) => {
            e.stopPropagation();
            onStart();
          }}
          disabled={isDisabled}
          sx={{
            minWidth: 130,
            backgroundColor: buttonColor,
            '&:hover': {
              backgroundColor:
                status === 'full' ? 'success.dark' :
                status === 'partial' ? 'warning.dark' :
                status === 'none' ? 'error.dark' : 'grey.600',
            },
            '&.Mui-disabled': {
              backgroundColor: status === 'none' ? 'error.light' : 'grey.400',
              color: 'white',
            },
          }}
        >
          {buttonText}
        </Button>
      </span>
    </Tooltip>
  );
}
